import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import CardSelectionController from 'src/components/Dashboard/CardSelectionController';
import {
  buildChartViewKey, getCardSelectionState, getChartSummaryData, selectedKeysToggleKey,
} from 'src/components/Dashboard/helpers/common';
import Loading from 'src/components/Loading';
import { APIConfig } from 'src/config';
import Chart from 'src/enosikit/components/Chart';
import Cards from 'src/enosikit/components/Chart/components/Cards';
import { meterContent, tradeContent } from 'src/enosikit/components/Chart/helpers/cards';
import { getTotalCarbonEmission } from 'src/lib/carbondata/helpers';
import FlashesStore from 'src/stores/FlashesStore';
import {
  DATA_AGGREGATE_BY_PORTFOLIO, DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE,
  EXPORTS, IMPORTS, SOURCE,
  SOURCE_HISTORIAN, SOURCE_TRADES,
  UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY,
} from 'src/util/constants';

import { meterChartTooltip, tradeChartTooltip } from './helpers/chartTooltip';
import {
  buildCardsData, buildChartData, buildMeterViewCardLabel, buildTradeViewCardLabel,
} from './helpers/portfolioShowChart';

/**
 * Maximum number of cards that can be selected before showing a warning to the user.
 *
 * This is a known limitation based on browser URL length constraints. When card selections
 * are stored in URL parameters (for state persistence and sharing), selecting too many cards
 * can exceed browser URL limits, particularly Firefox's 65,536 character limit.
 * Firefox's 65,536 character limit is the most restrictive constraint
 * actively-used modern browsers.
 *
 * The limit of 1750 cards was chosen as a conservative threshold that:
 * - Provides a reasonable buffer before reaching browser limits
 * - Accounts for URL encoding overhead and other URL parameters
 * - Balances user flexibility with technical constraints
 * - Based on ~37 characters per UUID in URL-encoded form
 *
 * When this limit is exceeded:
 * - A warning flash message is displayed to inform the user of potential issues
 * - The selection is still allowed to proceed (non-blocking warning)
 * - Users can make their own choice about whether to continue selecting more cards
 *
 * This is an intentional design decision to balance user experience with technical constraints.
 * @constant {number}
 * @see {@link https://medium.com/@python-javascript-php-html-css/recognizing-the-maximum-url-length-for-different-browsers-ff275903b970}
 */
const MAX_CARD_SELECTION_COUNT = 1750;

/**
 * Returns the PortfolioShowChart component
 * @param {object} props
 * @param {object} props.mainData MainData for the portfolio dashboard.
 * @param {object} props.chartView Grouping and aggregation options for the chart.
 * @param {DATA_AGGREGATE_BY_PROPERTY | DATA_AGGREGATE_BY_PORTFOLIO} props.chartView.aggregateBy
 * @param {DATA_GROUP_BY_COUNTERPARTY | DATA_GROUP_BY_TRADE_TYPE} props.chartView.groupBy
 * @param {Function} props.controlSetStateFunc Set the meter or the trade view.
 * @param {string} props.historianAggregation Aggregation for the chart.
 * @param {SOURCE_HISTORIAN | SOURCE_TRADES} props.source MainData for the portfolio dashboard
 * @param {object} props.timespan - An object defining the start and end dates for the timespan.
 * @param {Function} props.timespanUpdateFunc Gives the ability to update the timespan.
 * @param {string} props.timezone - The timezone for the chart.
 * @param {string} props.tradeAggregation The aggregation method for the trade data.
 * @param {UNIT_ENERGY| UNIT_CARBON | UNIT_CURRENCY} props.unit The unit for the chart.
 * @param {object} props.selectedKeys The selected keys for the chart.
 * @param {Function} props.setSelectedKeysFunc The function to set the selected keys for the chart.
 * @param {string} [props.noDataMessage] - Message to display when the user cancelled
 * a request and there is no current data available.
 * @returns {React.ReactComponentElement} PortfolioShowChart
 */
function PortfolioShowChart({ noDataMessage = null, ...props }) {
  const intl = useIntl();
  if (!props?.mainData) {
    return <Loading />;
  }
  const {
    chartView, controlSetStateFunc, mainData, historianAggregation,
    source, timespan, timespanUpdateFunc, timezone,
    tradeAggregation, unit, selectedKeys, setSelectedKeysFunc,
  } = props;

  const chartRef = useRef(true);

  const [chartSize, setChartSize] = useState({ height: 0, width: 0 });
  const [hoverKeys, setHoverKeys] = useState([]);

  const [tooltipTimestamp, setTooltipTimestamp] = useState(null);

  // Create a chart view identifier that includes all properties of chartView
  const chartViewKey = buildChartViewKey(chartView, source);
  const mode = APIConfig().MODE;
  const meterView = source === SOURCE_HISTORIAN;
  const tradeView = source === SOURCE_TRADES;
  const aggByProperty = chartView.aggregateBy === DATA_AGGREGATE_BY_PROPERTY;

  const { hasCounterfactual } = mainData;
  const chartData = buildChartData(intl, meterView, mainData, timezone, chartView);
  const chartSummaryData = getChartSummaryData(mainData);
  const cardsData = buildCardsData(meterView, chartData, chartView);
  const totalCarbonEmission = getTotalCarbonEmission(mainData.buy?.data);
  const { buy: buySummary, sell: sellSummary } = chartSummaryData;

  const {
    ChartControls, ChartMeter, ChartSummary, ChartTrade,
  } = Chart;

  /**
   * Handles the hover state for the cards in the PortfolioShowChart component.
   * @param {string} key The unique identifier of the card being hovered over.
   * @param {boolean} hover Indicates whether the card is being hovered over or not.
   * @returns {void}
   */
  const cardsHoverFunc = (key, hover) => {
    setHoverKeys((prevHoverKeys) => {
      const newHoverKeys = Array.isArray(prevHoverKeys) ? [...prevHoverKeys] : [];

      if (hover) {
        if (!newHoverKeys.includes(key)) {
          newHoverKeys.push(key);
        }
        return newHoverKeys;
      }
      const index = newHoverKeys.indexOf(key);
      if (index !== -1) {
        newHoverKeys.splice(index, 1);
      }

      return newHoverKeys;
    });
  };

  /**
   * Handles the selection state for the cards in the PortfolioShowChart component.
   *
   * This function toggles the selection state of a card identified by its key. When a user
   * attempts to select more than MAX_CARD_SELECTION_COUNT cards, a warning flash message
   * is displayed to inform them of potential browser URL length issues. However, the selection
   * is still allowed to proceed, giving users the choice to continue despite the warning.
   *
   * This is a known limitation and intentional design decision:
   * - Users receive a non-blocking warning when exceeding the recommended limit
   * - The selection proceeds anyway, respecting user choice
   * - Deselection always works without any warnings
   * @param {string} key The unique identifier of the card being selected or deselected.
   * @returns {void}
   * @see MAX_CARD_SELECTION_COUNT for details on the selection limit
   */
  const cardsSelectedFunc = (key) => {
    setSelectedKeysFunc((prevState) => {
      // Get current selected keys for this view
      const currentViewKeys = chartViewKey && prevState[chartViewKey]
        ? prevState[chartViewKey]
        : [];

      // Check if we're adding a new key (not removing)
      const isAddingKey = !currentViewKeys.includes(key);

      // If adding would exceed the limit, show warning but still allow selection
      if (isAddingKey && currentViewKeys.length >= MAX_CARD_SELECTION_COUNT) {
        const warningMessage = intl.formatMessage(
          {
            id: 'portfolio.portfolio_show.chart.selection_limit_exceeded.warning',
            defaultMessage: 'The card selection is too large ({currentCount}/{maxCount}) to be preserved when sharing a link to this dashboard in some browsers',
          },
          {
            currentCount: currentViewKeys.length,
            maxCount: MAX_CARD_SELECTION_COUNT,
          },
        );
        FlashesStore.flash(FlashesStore.WARNING, warningMessage);
      }

      return selectedKeysToggleKey(prevState, chartViewKey, key);
    });
  };

  /**
   * Set the boundary for the bar chart.
   * @returns {void}
   */
  const setChartBounds = useCallback(() => {
    if (!chartRef.current) {
      return;
    }

    let { clientHeight: height, clientWidth: width } = chartRef.current;

    const {
      paddingTop, paddingBottom, paddingLeft, paddingRight,
    } = window.getComputedStyle(chartRef.current);

    if (paddingTop !== '') { height -= parseFloat(paddingTop); }
    if (paddingBottom !== '') { height -= parseFloat(paddingBottom); }
    if (paddingLeft !== '') { width -= parseFloat(paddingLeft); }
    if (paddingRight !== '') { width -= parseFloat(paddingRight); }

    setChartSize({ height, width });
  }, []);

  /**
   * Provides the control options for the chart in the PortfolioShowChart component.
   * @returns {object[]} An array of control option objects.
   */
  const controlOptionFunc = () => {
    const typeOpts = {
      group: SOURCE,
      items: [{
        value: SOURCE_TRADES, label: <FormattedMessage id="portfolio.portfolio_show.chart.control.source.trades.title" defaultMessage="Trades" />, active: (source === SOURCE_TRADES), disabled: false,
      }, {
        value: SOURCE_HISTORIAN, label: <FormattedMessage id="portfolio.portfolio_show.chart.control.source.historian.title" defaultMessage="Meter" />, active: (source === SOURCE_HISTORIAN), disabled: false,
      }],
    };
    return [typeOpts];
  };

  useEffect(() => {
    // Victory sets the SVG width attribute from the `width` prop. When the
    // actual container width changes (sidebar toggles, CSS transitions, layout
    // shifts) but the prop stays stale, the browser scales the entire SVG to
    // fit — making axis labels and tooltip text appear larger or smaller even
    // though their font-size attribute is unchanged.
    //
    // The previous window 'resize' listener only fired on browser-window
    // resizes, missing container-only changes. ResizeObserver fires whenever
    // the observed element's dimensions change for any reason, keeping the
    // width prop in sync and preventing the SVG scaling mismatch.
    const node = chartRef.current;
    if (!node) return undefined;

    // Measure immediately on mount so the first render has accurate dimensions.
    setChartBounds();

    const resizeObserver = new ResizeObserver(() => {
      setChartBounds();
    });
    resizeObserver.observe(node);
    return () => resizeObserver.disconnect();
  }, [setChartBounds]);

  /**
   * Handles the timespan update when a bar(in the chart) is clicked.
   * @returns {void}
   */
  const handleChartClick = () => {
    if (tooltipTimestamp === null || historianAggregation.match(/^\d+S$/)) {
      return;
    }
    timespanUpdateFunc(tooltipTimestamp, tooltipTimestamp);
  };

  /**
   * Renders the content for a meter card.
   * @param {object} cardDatum An object containing the data for the meter card.
   * @param {object=} cardDatum.property The property that is part of the portfolio.
   * @param {object=} cardDatum.counterParty The counterparty.
   * @param {object=} cardDatum.counterPartyProperty The coutnerparty's property.
   * @param {object} cardDatum.buy The buy object for the meter card.
   * @param {object} cardDatum.sell The sell object for the meter card.
   * @returns {React.ReactElement|null} The rendered meter card content.
   */
  const meterViewCard = (cardDatum) => {
    const { property, buy, sell } = cardDatum;
    const { volume: buyVolume } = buy;
    const { volume: sellVolume } = sell;
    const cardLabel = buildMeterViewCardLabel(property, timespan, timezone, chartView);

    return meterContent(intl, cardLabel, buyVolume, sellVolume);
  };

  /**
   * Renders the content for a trade card.
   * @param {object} cardDatum An object containing the data for the trade card.
   * @param {object=} cardDatum.counterPartyUser Counterparty.
   * @param {object=} cardDatum.counterPartyProperty Counterparty's property (if visible).
   * @param {object=} cardDatum.property property object.
   * @param {object=} cardDatum.tradeType trade type for the card.
   * @returns {React.ReactElement} The rendered trade card content.
   */
  const tradeViewCard = (cardDatum) => {
    const { tradeType } = cardDatum;
    const cardLabel = buildTradeViewCardLabel(intl, cardDatum, chartView, timezone, timespan);

    return tradeContent(intl, tradeType, cardDatum, cardLabel);
  };

  const {
    finalSelectedKeys,
    hasCurrentViewSelectedKeys,
    hasAnySelectedKeys,
    enableClearBtn,
    cardsDataAvailable,
  } = getCardSelectionState(selectedKeys, chartViewKey, cardsData);

  return (
    <>
      <div className="mt-4 mb-4 card">
        <div className="card-body" ref={chartRef}>
          <div className="card-body d-flex justify-content-between">
            <div className="flex-fill justify-content-center order-2 order-xs-3 col-xs-12">
              <ChartControls
                buttons={controlOptionFunc()}
                onButtonClick={(opts) => { controlSetStateFunc(opts); }}
              />
            </div>
            <div className="flex-fill order-1 order-xs-1 col-xs-12">
              <ChartSummary
                align="left"
                category={IMPORTS}
                carbonData={totalCarbonEmission}
                counterfactual={buySummary.netDiffValue}
                hasCounterfactual={hasCounterfactual}
                mode={mode}
                tradedEnergy={buySummary.tradedEnergy}
                tradedValue={buySummary.tradedValue}
                untradedEnergy={buySummary.untradedVolume}
                untradedValue={buySummary.untradedValue}
                rebatedEnergy={buySummary.rebatedEnergy}
              />
            </div>
            <div className="flex-fill order-3 order-xs-2 col-xs-12">
              <ChartSummary
                align="right"
                category={EXPORTS}
                counterfactual={sellSummary.netDiffValue}
                hasCounterfactual={hasCounterfactual}
                mode={mode}
                tradedEnergy={sellSummary.tradedEnergy}
                tradedValue={sellSummary.tradedValue}
                untradedEnergy={sellSummary.untradedVolume}
                untradedValue={sellSummary.untradedValue}
                rebatedEnergy={sellSummary.rebatedEnergy}
              />
            </div>
          </div>
          {noDataMessage && (
            <div className="d-flex justify-content-center align-items-center">
              <div className="alert alert-warning">
                {noDataMessage}
              </div>
            </div>
          )}
          {tradeView && !noDataMessage && (
            <ChartTrade
              aggregation={tradeAggregation}
              chartView={chartView}
              handleChartClick={() => handleChartClick()}
              hoverKeys={hoverKeys}
              selectedKeys={finalSelectedKeys}
              timespan={timespan}
              timezone={timezone}
              tooltipLabelFunc={tradeChartTooltip}
              tooltipTimestamp={tooltipTimestamp}
              tooltipUpdate={(timestamp) => setTooltipTimestamp(timestamp)}
              tradeDataBuy={chartData.buy}
              tradeDataSell={chartData.sell}
              unit={unit}
              width={chartSize.width}
            />
          )}
          {meterView && !noDataMessage && (
            <ChartMeter
              aggregation={historianAggregation}
              chartView={chartView}
              data={chartData}
              handleChartClick={() => handleChartClick()}
              hoverKeys={hoverKeys}
              isByMeter={aggByProperty}
              selectedKeys={finalSelectedKeys}
              timespan={timespan}
              timezone={timezone}
              tooltipLabelFunc={meterChartTooltip}
              tooltipTimestamp={tooltipTimestamp}
              tooltipUpdate={(timestamp) => setTooltipTimestamp(timestamp)}
              unit={unit}
              width={chartSize.width}
            />
          )}
        </div>
      </div>
      {cardsDataAvailable && (
        <div className="d-flex justify-content-end mb-2">
          <CardSelectionController
            enableClearBtn={enableClearBtn}
            hasCurrentViewSelectedKeys={hasCurrentViewSelectedKeys}
            hasAnySelectedKeys={hasAnySelectedKeys}
            onClearCurrentSelectionFunc={() => setSelectedKeysFunc(
              (prev) => ({ ...prev, [chartViewKey]: [] }),
            )}
            onClearAllSelectionsFunc={() => setSelectedKeysFunc({})}
          />
        </div>
      )}

      {(meterView && cardsDataAvailable) && (
        <Cards
          cardFunc={meterViewCard}
          data={cardsData}
          hoverFunc={cardsHoverFunc}
          hoverKeys={hoverKeys}
          selectedFunc={cardsSelectedFunc}
          selectedKeys={finalSelectedKeys}
        />
      )}
      {(tradeView && cardsDataAvailable) && (
        <Cards
          cardFunc={tradeViewCard}
          data={cardsData}
          hoverFunc={cardsHoverFunc}
          hoverKeys={hoverKeys}
          selectedFunc={cardsSelectedFunc}
          selectedKeys={finalSelectedKeys}
        />
      )}
    </>
  );
}

PortfolioShowChart.propTypes = {
  chartView: PropTypes.shape({
    aggregateBy: PropTypes.oneOf([
      DATA_AGGREGATE_BY_PORTFOLIO,
      DATA_AGGREGATE_BY_PROPERTY,
    ]).isRequired,
    groupBy: PropTypes.oneOf([
      DATA_GROUP_BY_COUNTERPARTY,
      DATA_GROUP_BY_TRADE_TYPE,
    ]).isRequired,
  }).isRequired,
  controlSetStateFunc: PropTypes.func.isRequired,
  historianAggregation: PropTypes.string.isRequired,
  mainData: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  noDataMessage: PropTypes.string,
  source: PropTypes.oneOf([SOURCE_HISTORIAN, SOURCE_TRADES]).isRequired,
  selectedKeys: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  setSelectedKeysFunc: PropTypes.func.isRequired,
  timespan: PropTypes.shape({
    start: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    finish: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }).isRequired,
  timespanUpdateFunc: PropTypes.func.isRequired,
  timezone: PropTypes.string.isRequired,
  tradeAggregation: PropTypes.string.isRequired,
  unit: PropTypes.oneOf([UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY]).isRequired,
};

export default PortfolioShowChart;
